import React, { useEffect, useState } from 'react';
import * as unidadesApi from '../../api/unidadesMedida';
import { useEmpresaSelecionada } from '../../contexts/EmpresaContext';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';
import Skeleton from '../../components/common/Skeleton';
import SearchableSelect from '../../components/common/SearchableSelect';
import StatusBadge from '../../components/common/StatusBadge';
import { useSortable } from '../../hooks/useSortable';
import { getErrorMessage } from '../../utils/apiError';

const VAZIO = { nome: '', sigla: '' };

export default function Unidades() {
  const { modoGlobal, empresas, idEmpresa } = useEmpresaSelecionada();

  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [form, setForm] = useState(VAZIO);
  const [idEmpresaForm, setIdEmpresaForm] = useState('');
  const [editando, setEditando] = useState(null); // item completo, não só o id
  const [mostrarForm, setMostrarForm] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [mostrarInativos, setMostrarInativos] = useState(false);

  const carregar = () => {
    setLoading(true);
    unidadesApi
      .listUnidades(mostrarInativos)
      .then((r) => setItens(r.data))
      .catch(() => setErro('Erro ao carregar unidades de medida.'))
      .finally(() => setLoading(false));
  };

  // idEmpresa muda quando o super_admin troca de empresa no seletor do topbar — sem isso, a
  // lista continuaria mostrando os cadastros da empresa anterior até uma navegação manual.
  useEffect(carregar, [mostrarInativos, idEmpresa]);

  const { sorted, Th } = useSortable(itens, 'nome');

  const abrirNovo = () => {
    setForm(VAZIO);
    setIdEmpresaForm('');
    setEditando(null);
    setMostrarForm(true);
  };

  const abrirEdicao = (item) => {
    setForm({ nome: item.nome, sigla: item.sigla });
    setEditando(item);
    setMostrarForm(true);
  };

  const alternarAtivo = async (item) => {
    const acao = item.ativo ? 'Desativar' : 'Reativar';
    const aviso = item.ativo ? ' Ela deixa de aparecer nesta lista e nos seletores de busca.' : '';
    if (!window.confirm(`${acao} "${item.nome}"?${aviso}`)) return;
    setErro('');
    try {
      await unidadesApi.updateUnidade(item.id_unidade_medida, { ativo: !item.ativo }, item.id_empresa);
      carregar();
    } catch (err) {
      setErro(getErrorMessage(err, 'Erro ao alterar o status da unidade.'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    if (!editando && modoGlobal && !idEmpresaForm) {
      setErro('Selecione a empresa dona deste cadastro.');
      return;
    }
    setSalvando(true);
    try {
      if (editando) await unidadesApi.updateUnidade(editando.id_unidade_medida, form, editando.id_empresa);
      else await unidadesApi.createUnidade(form, idEmpresaForm);
      setMostrarForm(false);
      carregar();
    } catch (err) {
      setErro(getErrorMessage(err, 'Erro ao salvar.'));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div>
      <PageHeader titulo="Unidades de Medida" subtitulo="Saco, Kg, Ramo, Peça...">
        {!mostrarForm && (
          <button type="button" className="sf-btn sf-btn-primary" onClick={abrirNovo}>
            <i className="bi bi-plus-lg" />
            Nova unidade
          </button>
        )}
      </PageHeader>

      {erro && (
        <div className="sf-login-alert mb-3">
          <i className="bi bi-exclamation-circle" />
          {erro}
        </div>
      )}

      {mostrarForm && (
        <form onSubmit={handleSubmit} className="sf-card p-4 mb-3" style={{ maxWidth: 480 }}>
          {editando && modoGlobal && (
            <div className="sf-badge sf-badge-neutral sf-badge-plain mb-3">
              <i className="bi bi-building" /> {editando.empresa.nome}
            </div>
          )}
          {!editando && modoGlobal && (
            <div className="sf-field-label mb-3">
              <span>Empresa</span>
              <SearchableSelect
                options={empresas.map((e) => ({ value: e.id_empresa, label: e.nome }))}
                value={idEmpresaForm}
                onChange={setIdEmpresaForm}
                placeholder="Selecione a empresa..."
                required
              />
            </div>
          )}
          <div className="row g-3">
            <div className="col-8">
              <label className="sf-field-label">
                <span>Nome</span>
                <span className="sf-field">
                  <input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                </span>
              </label>
            </div>
            <div className="col-4">
              <label className="sf-field-label">
                <span>Sigla</span>
                <span className="sf-field">
                  <input required maxLength={10} value={form.sigla} onChange={(e) => setForm({ ...form, sigla: e.target.value })} />
                </span>
              </label>
            </div>
          </div>
          <div className="mt-3 d-flex gap-2">
            <button type="submit" className="sf-btn sf-btn-primary" disabled={salvando}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
            <button type="button" className="sf-btn sf-btn-ghost" onClick={() => setMostrarForm(false)}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="sf-table-card">
        <div className="sf-table-toolbar">
          <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={mostrarInativos} onChange={(e) => setMostrarInativos(e.target.checked)} />
            Mostrar inativas
          </label>
        </div>
        {loading ? (
          <div className="p-4">
            <Skeleton linhas={5} />
          </div>
        ) : sorted.length === 0 ? (
          <EmptyState icon="bi-rulers" titulo="Nenhuma unidade cadastrada" texto="Comece cadastrando a primeira unidade de medida." />
        ) : (
          <div className="table-responsive">
            <table className="sf-table">
              <thead>
                <tr>
                  <Th campo="nome">Nome</Th>
                  <th>Sigla</th>
                  {modoGlobal && <th>Empresa</th>}
                  {mostrarInativos && <th>Status</th>}
                  <th />
                </tr>
              </thead>
              <tbody>
                {sorted.map((u) => (
                  <tr key={u.id_unidade_medida}>
                    <td>
                      <span className="d-flex align-items-center gap-2">
                        <span className="sf-cell-icon"><i className="bi bi-rulers" /></span>
                        <span className="sf-cell-title">{u.nome}</span>
                      </span>
                    </td>
                    <td>{u.sigla}</td>
                    {modoGlobal && <td className="sf-cell-sub">{u.empresa.nome}</td>}
                    {mostrarInativos && <td><StatusBadge status={u.ativo ? 'ativo' : 'inativo'} /></td>}
                    <td className="text-end">
                      <div className="d-flex gap-2 justify-content-end">
                        <button type="button" className="sf-btn sf-btn-ghost" onClick={() => abrirEdicao(u)} title="Editar">
                          <i className="bi bi-pencil" />
                        </button>
                        <button
                          type="button"
                          className="sf-btn sf-btn-ghost"
                          style={u.ativo ? { color: 'var(--sf-danger-fg)', borderColor: '#f0cfcc' } : undefined}
                          onClick={() => alternarAtivo(u)}
                          title={u.ativo ? 'Desativar' : 'Reativar'}
                        >
                          <i className={`bi ${u.ativo ? 'bi-slash-circle' : 'bi-check-circle'}`} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
